"use client";

import { useEffect, useRef } from "react";

/**
 * Fires once, when the element it is attached to actually reaches the screen.
 *
 * A product list that is reported the moment its data arrives inflates the
 * impression count with rows nobody scrolled down to, and the click rate built
 * on top of it comes out lower than the truth. So the report waits for the list
 * to be seen.
 *
 * Returns a ref to put on the list container, or on a thin sentinel sitting
 * where the list begins. Pass a stable onSeen — a useCallback — or the observer
 * is rebuilt on every render.
 */
export function useListImpression(onSeen: () => void, ready: boolean) {
    const ref = useRef<HTMLDivElement | null>(null);
    const seen = useRef(false);

    useEffect(() => {
        if (!ready || seen.current) return;

        const node = ref.current;
        if (!node) return;

        // An old browser without the observer still gets counted, just without
        // the visibility check. Losing the list entirely would be worse.
        if (typeof IntersectionObserver === "undefined") {
            seen.current = true;
            onSeen();
            return;
        }

        const observer = new IntersectionObserver((entries) => {
            if (!entries.some(entry => entry.isIntersecting)) return;
            if (seen.current) return;

            seen.current = true;
            observer.disconnect();
            onSeen();
        });

        observer.observe(node);

        return () => observer.disconnect();
    }, [ready, onSeen]);

    return ref;
}
